import app from 'flarum/forum/app';
import Page from 'flarum/common/components/Page';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import LineChart from './LineChart';

/**
 * /data-hub/ecowas-trade: the 12 ECOWAS members ranked by exports, overall or for one commodity; one country's
 * breakdown and trend; trade between members (a matrix, the biggest flows, the trend); and the bloc's totals.
 * Mali, Burkina Faso and Niger (left in 2025) can be shown alongside, unranked. Data: UN Comtrade (TradeData).
 */
const go = (e, url) => {
  if (e.ctrlKey || e.metaKey) return;
  e.preventDefault();
  m.route.set(url);
};
const usd = v => {
  if (v === null || v === undefined) return '—';
  const a = Math.abs(v);
  if (a >= 1e9) return '$' + (v / 1e9).toFixed(a >= 1e10 ? 1 : 2).replace(/\.?0+$/, '') + 'bn';
  if (a >= 1e6) return '$' + (v / 1e6).toFixed(a >= 1e8 ? 0 : 1).replace(/\.0$/, '') + 'm';
  if (a >= 1e3) return '$' + Math.round(v / 1e3) + 'k';
  return '$' + Math.round(v);
};
const pct = (x, digits = 1) => x === null || x === undefined || !isFinite(x) ? '—' : (x * 100).toFixed(digits).replace(/\.0$/, '') + '%';
const sum = arr => arr.reduce((a, b) => a + (b || 0), 0);
export default class EcowasTradePage extends Page {
  oninit(vnode) {
    super.oninit(vnode);
    this.bodyClass = 'DataHubFull';
    this.data = null;
    this.year = null;
    this.commodity = 'TOTAL';
    this.showAes = false;
    this.selected = null;
    app.setTitle('ECOWAS export rankings');
    app.request({
      method: 'GET',
      url: app.forum.attribute('apiUrl') + '/datahub/ecowas-trade',
      errorHandler: () => {}
    }).then(r => {
      this.data = r;
      this.year = r.latest;
    }).catch(() => this.data = false).finally(() => m.redraw());
  }

  // ---------- data helpers ----------
  members() {
    return this.data.countries.filter(c => c.member);
  }
  name(iso) {
    const c = this.data.countries.find(x => x.iso === iso);
    return c ? c.name : iso;
  }
  ex(y, iso, key = 'TOTAL') {
    const e = (this.data.exports[y] || {})[iso];
    return e && e[key] !== undefined ? e[key] : null;
  }
  estimated(y, iso) {
    const e = (this.data.exports[y] || {})[iso];
    return !!(e && e._basis === 'mirror');
  }
  imp(y, iso) {
    const v = (this.data.imports[y] || {})[iso];
    return v === undefined ? null : v;
  }

  /** Exports between members in a year: {from: {to: value}} (members only) */
  intra(y) {
    const out = {};
    const isMember = iso => this.members().some(c => c.iso === iso);
    Object.entries(this.data.intra[y] || {}).forEach(([from, tos]) => {
      if (!isMember(from)) return;
      Object.entries(tos).forEach(([to, v]) => {
        if (isMember(to)) (out[from] = out[from] || {})[to] = v;
      });
    });
    return out;
  }
  intraTotal(y) {
    return sum(Object.values(this.intra(y)).map(tos => sum(Object.values(tos))));
  }
  bloc(y) {
    const isos = this.members().map(c => c.iso);
    return {
      exports: sum(isos.map(i => this.ex(y, i))),
      imports: sum(isos.map(i => this.imp(y, i))),
      intra: this.intraTotal(y),
      reporting: isos.filter(i => this.ex(y, i) !== null).length,
      estimated: isos.filter(i => this.estimated(y, i)).length
    };
  }

  /** Members ranked by the chosen commodity (or total) in the chosen year */
  ranking() {
    const y = this.year;
    const key = this.commodity;
    const rows = this.members().map(c => ({
      iso: c.iso,
      name: c.name,
      value: this.ex(y, c.iso, key),
      prev: this.ex(y - 1, c.iso, key),
      est: this.estimated(y, c.iso)
    })).filter(r => r.value !== null && r.value > 0).sort((a, b) => b.value - a.value);
    const total = sum(rows.map(r => r.value));
    rows.forEach((r, i) => {
      r.rank = i + 1;
      r.share = total ? r.value / total : null;
    });
    const aes = this.showAes ? this.data.countries.filter(c => !c.member).map(c => ({
      iso: c.iso,
      name: c.name,
      value: this.ex(y, c.iso, key),
      prev: this.ex(y - 1, c.iso, key),
      est: this.estimated(y, c.iso),
      aes: true
    })).filter(r => r.value !== null && r.value > 0) : [];
    return {
      rows,
      total,
      aes
    };
  }

  // ---------- view ----------
  view() {
    const d = this.data;
    return m("div", {
      className: "DataHubFull-page DHTrade"
    }, m("section", {
      className: "DHHero DHState-hero"
    }, m("div", {
      className: "container DHHero-inner"
    }, m("div", {
      className: "DHHero-text"
    }, m("a", {
      className: "DataHubPub-back",
      href: "/data-hub",
      onclick: e => go(e, '/data-hub')
    }, m("i", {
      className: "fas fa-arrow-left"
    }), " Data Hub"), m("div", {
      className: "DHHero-eyebrow"
    }, "ECOWAS trade"), m("h1", null, "ECOWAS export rankings"), m("p", null, "How West Africa's 12 ECOWAS members rank on exports, commodity by commodity, and how much they trade with each other."), d && d.years.length ? m("label", {
      className: "DHTrade-year"
    }, "Year", m("select", {
      className: "FormControl",
      value: this.year,
      onchange: e => {
        this.year = Number(e.target.value);
      }
    }, d.years.slice().reverse().map(y => m("option", {
      value: y
    }, y)))) : null))), m("div", {
      className: "container DHState"
    }, d === null ? m(LoadingIndicator, null) : !d || !d.years.length ? m("p", {
      className: "DHMuted"
    }, "Trade figures are on their way: they're collected from UN Comtrade, and the first run is under way.") : [this.totals(), this.rankings(), this.country(), this.between(), this.method()]));
  }
  totals() {
    const y = this.year;
    const b = this.bloc(y);
    const prev = this.data.years.includes(y - 1) ? this.bloc(y - 1) : null;
    const change = prev && prev.exports ? b.exports / prev.exports - 1 : null;
    // biggest exporter overall, whatever commodity is picked below
    const tops = this.members().map(c => ({
      name: c.name,
      value: this.ex(y, c.iso) || 0
    })).sort((p, q) => q.value - p.value);
    const top = tops[0] && tops[0].value ? {
      name: tops[0].name,
      share: b.exports ? tops[0].value / b.exports : null
    } : null;
    return m("section", {
      className: "DHSection"
    }, m("div", {
      className: "DHSection-head"
    }, m("div", null, m("h2", null, "ECOWAS in ", y), m("p", {
      className: "DHMuted"
    }, "The 12 members together. ", b.reporting < 12 ? b.reporting + ' of 12 have figures for ' + y + '. ' : '', b.estimated ? b.estimated + ' estimated from their partners\' imports.' : ''))), m("div", {
      className: "DHTrade-kpis"
    }, m("div", {
      className: "DHTrade-kpi"
    }, m("small", null, "Exports to the world"), m("strong", null, usd(b.exports)), change !== null ? m("span", {
      className: change >= 0 ? 'is-up' : 'is-down'
    }, change >= 0 ? '▲' : '▼', " ", pct(Math.abs(change)), " on ", y - 1) : null), b.imports ? m("div", {
      className: "DHTrade-kpi"
    }, m("small", null, "Imports from the world"), m("strong", null, usd(b.imports)), m("span", null, b.exports && b.imports ? (b.exports >= b.imports ? 'Surplus ' : 'Deficit ') + usd(Math.abs(b.exports - b.imports)) : '')) : null, b.intra ? m("div", {
      className: "DHTrade-kpi"
    }, m("small", null, "Trade between members"), m("strong", null, usd(b.intra)), m("span", null, b.exports ? pct(b.intra / b.exports) + ' of their exports' : '')) : null, top ? m("div", {
      className: "DHTrade-kpi"
    }, m("small", null, "Biggest exporter"), m("strong", null, top.name), m("span", null, pct(top.share, 0) + ' of ECOWAS exports')) : null));
  }
  rankings() {
    const y = this.year;
    const groups = this.data.groups.map(g => ({
      ...g,
      total: sum(this.members().map(c => this.ex(y, c.iso, g.key)))
    })).filter(g => g.total > 0).sort((a, b) => b.total - a.total);
    const {
      rows,
      total,
      aes
    } = this.ranking();
    const max = Math.max(1, ...rows.map(r => r.value), ...aes.map(r => r.value));
    const label = this.commodity === 'TOTAL' ? 'all exports' : (this.data.groups.find(g => g.key === this.commodity) || {}).label;
    const line = r => m("button", {
      type: "button",
      className: 'DHTrade-rankRow' + (r.aes ? ' is-aes' : '') + (this.selected === r.iso ? ' is-active' : ''),
      onclick: () => {
        this.selected = r.iso;
        setTimeout(() => {
          const el = document.getElementById('trade-country');
          if (el) window.scrollTo({
            top: el.getBoundingClientRect().top + window.pageYOffset - 90,
            behavior: 'smooth'
          });
        }, 30);
      }
    }, m("span", {
      className: "DHTrade-rank"
    }, r.aes ? '—' : '#' + r.rank), m("span", {
      className: "DHTrade-name"
    }, m("strong", null, r.name), r.aes ? m("small", null, "Left ECOWAS in 2025") : r.est ? m("small", null, "Estimated from partners' imports") : null), m("span", {
      className: "DHTrade-bar"
    }, m("span", {
      style: {
        width: 100 * r.value / max + '%'
      }
    })), m("span", {
      className: "DHTrade-val"
    }, m("strong", null, usd(r.value)), m("small", null, r.aes ? '' : pct(r.share, 0) + ' of ECOWAS', r.prev ? ' · ' + (r.value >= r.prev ? '▲' : '▼') + pct(Math.abs(r.value / r.prev - 1), 0) : '')));
    return m("section", {
      className: "DHSection"
    }, m("div", {
      className: "DHSection-head"
    }, m("div", null, m("h2", null, "Export rankings"), m("p", {
      className: "DHMuted"
    }, "Members ranked by ", label, " in ", y, this.commodity !== 'TOTAL' ? ': ' + usd(total) + ' across ECOWAS' : '', ". Tap a country for its breakdown."))), m("div", {
      className: "DHTrade-chips",
      role: "tablist"
    }, m("button", {
      className: 'DHTrade-chip' + (this.commodity === 'TOTAL' ? ' is-on' : ''),
      onclick: () => this.commodity = 'TOTAL'
    }, "All exports"), groups.map(g => m("button", {
      className: 'DHTrade-chip' + (this.commodity === g.key ? ' is-on' : ''),
      onclick: () => this.commodity = g.key
    }, m("i", {
      className: g.icon,
      "aria-hidden": "true"
    }), " ", g.label.replace(/ \(.*\)$/, '')))), m("div", {
      className: "DHTrade-ranking"
    }, rows.length ? rows.map(line) : m("p", {
      className: "DHMuted"
    }, "No member recorded exports of this in ", y, "."), aes.map(line)), m("label", {
      className: "DHTrade-aes"
    }, m("input", {
      type: "checkbox",
      checked: this.showAes,
      onchange: e => this.showAes = e.target.checked
    }), " Show Mali, Burkina Faso and Niger (left ECOWAS in January 2025), not ranked"));
  }
  country() {
    const y = this.year;
    const iso = this.selected || (this.ranking().rows[0] || {}).iso;
    if (!iso) return null;
    const total = this.ex(y, iso);
    const parts = this.data.groups.map(g => ({
      label: g.label,
      icon: g.icon,
      value: this.ex(y, iso, g.key) || 0
    })).filter(p => p.value > 0).sort((a, b) => b.value - a.value);
    const other = total ? Math.max(0, total - sum(parts.map(p => p.value))) : 0;
    if (other > 0) parts.push({
      label: 'Everything else',
      icon: 'fas fa-ellipsis',
      value: other
    });
    const dest = Object.entries((this.data.intra[y] || {})[iso] || {}).filter(([to]) => this.members().some(c => c.iso === to)).sort((a, b) => b[1] - a[1]);
    const toMembers = sum(dest.map(x => x[1]));
    const years = this.data.years;
    const series = [{
      id: 1,
      name: 'Exports',
      points: years.filter(yy => this.ex(yy, iso) !== null).map(yy => ({
        period: String(yy),
        value: this.ex(yy, iso) / 1e9
      }))
    }, {
      id: 2,
      name: 'Imports',
      points: years.filter(yy => this.imp(yy, iso) !== null).map(yy => ({
        period: String(yy),
        value: this.imp(yy, iso) / 1e9
      }))
    }];
    return m("section", {
      className: "DHSection",
      id: "trade-country"
    }, m("div", {
      className: "DHSection-head"
    }, m("div", null, m("h2", null, this.name(iso)), m("p", {
      className: "DHMuted"
    }, total !== null ? usd(total) + ' of exports in ' + y : 'No figures for ' + y, this.estimated(y, iso) ? ', estimated from its partners\' imports' : '', ".", ' ', total ? pct(toMembers / total) + ' went to other ECOWAS members.' : ''))), m("div", {
      className: "DHPair"
    }, parts.length ? m("div", {
      className: "DHPanel"
    }, m("h3", null, "What it exports"), m("ul", {
      className: "DHTrade-parts"
    }, parts.map(p => m("li", null, m("span", {
      className: "DHTrade-partLabel"
    }, m("i", {
      className: p.icon,
      "aria-hidden": "true"
    }), " ", p.label), m("span", {
      className: "DHTrade-bar"
    }, m("span", {
      style: {
        width: (total ? 100 * p.value / total : 0) + '%'
      }
    })), m("span", {
      className: "DHTrade-partVal"
    }, usd(p.value), " ", m("small", null, total ? pct(p.value / total, 0) : '')))))) : null, series[0].points.length > 1 || dest.length ? m("div", {
      className: "DHPanel"
    }, series[0].points.length > 1 ? [m("h3", null, "Exports and imports over time"), m("p", {
      className: "DHMuted"
    }, "US$ billion"), m(LineChart, {
      unit: "US$ bn",
      series: series
    })] : null, dest.length ? [m("h3", {
      className: series[0].points.length > 1 ? 'DHTrade-subhead' : ''
    }, "Biggest ECOWAS buyers"), m("ol", {
      className: "DHTrade-dest"
    }, dest.slice(0, 5).map(([to, v]) => m("li", null, m("span", null, this.name(to)), m("strong", null, usd(v)))))] : null) : null));
  }
  between() {
    const y = this.year;
    const flows = this.intra(y);
    const members = this.members().slice().sort((a, b) => (this.ex(y, b.iso) || 0) - (this.ex(y, a.iso) || 0));
    const all = [];
    Object.entries(flows).forEach(([from, tos]) => Object.entries(tos).forEach(([to, v]) => all.push({
      from,
      to,
      v
    })));
    all.sort((a, b) => b.v - a.v);
    const max = Math.max(1, ...all.map(f => f.v));
    const shade = v => v ? 0.08 + 0.92 * (Math.log10(v + 1) / Math.log10(max + 1)) : 0;
    const sel = this.selected;
    const trend = this.data.years.map(yy => ({
      period: String(yy),
      value: this.intraTotal(yy) / 1e9
    })).filter(p => p.value > 0);
    if (!all.length) return null;
    // only members with some recorded trade with the others (a country that hasn't reported, and that no member
    // reports trading with, would be an empty row and column)
    const active = members.filter(c => all.some(f => f.from === c.iso || f.to === c.iso));
    return m("section", {
      className: "DHSection"
    }, m("div", {
      className: "DHSection-head"
    }, m("div", null, m("h2", null, "Trade between members"), m("p", {
      className: "DHMuted"
    }, "What each member exported to the others in ", y, ": ", usd(this.intraTotal(y)), " in all. Rows sell, columns buy; darker means more."))), m("div", {
      className: "DHTrade-matrixWrap"
    }, m("table", {
      className: "DHTrade-matrix"
    }, m("thead", null, m("tr", null, m("th", {
      className: "DHTrade-corner"
    }, "From \u2193 To \u2192"), active.map(c => m("th", {
      className: sel === c.iso ? 'is-sel' : '',
      title: c.name
    }, m("span", null, c.iso))))), m("tbody", null, active.map(r => m("tr", {
      className: sel === r.iso ? 'is-sel' : ''
    }, m("th", {
      onclick: () => this.selected = r.iso
    }, r.name), active.map(c => {
      const v = r.iso === c.iso ? null : (flows[r.iso] || {})[c.iso] || 0;
      return r.iso === c.iso ? m("td", {
        className: "is-self"
      }) : m("td", {
        title: r.name + ' → ' + c.name + ': ' + usd(v),
        style: {
          background: v ? 'rgba(27, 69, 64, ' + shade(v).toFixed(2) + ')' : 'transparent'
        },
        className: (sel === c.iso ? 'is-sel ' : '') + (shade(v) > 0.55 ? 'is-dark' : '')
      }, v ? usd(v) : '');
    })))))), m("div", {
      className: "DHPair"
    }, m("div", {
      className: "DHPanel"
    }, m("h3", null, "Biggest flows in ", y), m("ol", {
      className: "DHTrade-dest"
    }, all.slice(0, 10).map(f => m("li", null, m("span", null, this.name(f.from), " \u2192 ", this.name(f.to)), m("strong", null, usd(f.v)))))), trend.length > 1 ? m("div", {
      className: "DHPanel"
    }, m("h3", null, "Trade between members over time"), m("p", {
      className: "DHMuted"
    }, "US$ billion"), m(LineChart, {
      unit: "US$ bn",
      series: [{
        id: 1,
        name: 'Between members',
        points: trend
      }]
    })) : null));
  }
  method() {
    return m("section", {
      className: "DHSection"
    }, m("div", {
      className: "DHPanel DHTrade-method"
    }, m("h3", null, "About these figures"), m("p", null, "From ", m("a", {
      href: "https://comtradeplus.un.org/",
      target: "_blank",
      rel: "noopener"
    }, "UN Comtrade"), ": the customs figures each country reports, in US dollars. Some countries report a year or more late; where a country hasn't reported a year, its exports are estimated from what other countries report importing from it, and marked. Those run a little high, because importers include freight and insurance."), m("p", null, "Commodities are grouped from customs (HS) codes, e.g. crude oil is HS 2709 and cocoa is HS chapter 18. ECOWAS is its 12 current members; Mali, Burkina Faso and Niger left in January 2025 and can be shown for comparison. Trade between members is often under-recorded, since much of it crosses borders informally."), this.data.updated ? m("p", {
      className: "DHMuted"
    }, "Last collected ", new Date(this.data.updated).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    }), ".") : null));
  }
}